import { useEffect, useState } from 'react'
import type { RoastTone } from '../lib/roasts'
import { openNavigatorHandoff } from '../lib/navigatorHandoff'
import type { TabSharePayload } from '../lib/shareCard'
import { shareTabCard } from '../lib/shareCard'
import { RoastCard } from './RoastCard'

const AUTO_DISMISS_MS = 14000

type Props = {
  text: string
  tone: RoastTone
  toastKey: number
  sharePayload: TabSharePayload | null
  onDismiss: () => void
}

/** Full-screen roast — only triggered after Save tab (see App.tsx). */
export function RoastToast({ text, tone, toastKey, sharePayload, onDismiss }: Props) {
  const [visible, setVisible] = useState(false)
  const [sharing, setSharing] = useState(false)

  useEffect(() => {
    if (toastKey <= 0) return
    setVisible(true)
    const timer = window.setTimeout(() => {
      setVisible(false)
      onDismiss()
    }, AUTO_DISMISS_MS)
    return () => window.clearTimeout(timer)
  }, [toastKey, onDismiss])

  if (!visible) return null

  const dismiss = () => {
    setVisible(false)
    onDismiss()
  }

  const onShare = async () => {
    if (!sharePayload || sharing) return
    setSharing(true)
    try {
      await shareTabCard(sharePayload)
    } finally {
      setSharing(false)
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex flex-col items-center justify-center gap-3 bg-black/65 p-5 backdrop-blur-[2px]"
      role="dialog"
      aria-modal="true"
      aria-label="Tab saved"
    >
      <button
        type="button"
        className="absolute inset-0 cursor-default"
        aria-label="Dismiss roast"
        onClick={dismiss}
      />
      <div
        className="roast-toast-panel relative z-10 w-full max-w-sm text-left"
        onClick={(event) => event.stopPropagation()}
      >
        <RoastCard text={text} tone={tone} showNavigatorCta={false} />
        <div className="mt-3 flex flex-col gap-2">
          {sharePayload ? (
            <button
              type="button"
              className="primary min-h-11 text-sm"
              disabled={sharing}
              onClick={() => void onShare()}
            >
              {sharing ? 'Preparing…' : 'Share tab card'}
            </button>
          ) : null}
          {tone === 'navigator' ? (
            <button
              type="button"
              className="secondary min-h-11 text-sm"
              onClick={() => void openNavigatorHandoff('get_home_roasted')}
            >
              Get home roasted →
            </button>
          ) : null}
          <button type="button" className="ghost min-h-10 text-sm" onClick={dismiss}>
            Done
          </button>
        </div>
      </div>
      <p className="relative z-10 text-center text-xs text-foam-muted">
        Tap outside to dismiss · {Math.round(AUTO_DISMISS_MS / 1000)}s
      </p>
    </div>
  )
}
